import { css, keyframes } from "@emotion/css";
import { colorScales, space, text, themeTokens } from "@octopusdeploy/design-system-tokens";
import { useState } from "react";
import { LoadingMessages } from "./LoadingMessages";

const messages = [
    "Subscribe to events, like license usage, to get alerts when nearing your billable resource limit.",
    "Octopus can securely store and manage certificates to use in your projects.",
    "Use scheduled triggers to create and automatically deploy releases, ideal for nightly QA deployments.",
    "Use scheduled triggers to schedule production deployments when customers aren't using your product.",
    "Automatically create and deploy releases based on changes in your Git repository with Git triggers.",
    "Use Deployment Freezes to block deployments during critical periods.",
].sort(() => Math.random() - 0.5);

export interface LoadingError {
    type: "server-unavailable" | "network-error";
    httpStatusCode?: number;
}

interface LoadingScreenProps {
    error?: LoadingError;
}

export function LoadingScreen({ error }: LoadingScreenProps) {
    const [showLoadingMessages, setShowLoadingMessages] = useState(false);
    const showError = error !== undefined;

    const handleSpinnerAnimationStart = () => {
        setShowLoadingMessages(true);
    };

    return (
        <div className={styles.container}>
            <div className={styles.spinnerContainer}>
                <svg className={svgStyles.octopusIcon} viewBox="0 0 140 140">
                    <path
                        d="M19.1 102.1c9.4-6 20.3-16.3 16.2-28.5-2.3-6.6-5.4-12.3-5.8-19.4-.4-6.1.5-12.2 2.6-17.9C40.5 13.8 65.4 3.2 87.9 10c20.8 6.3 35.2 30.5 26.6 51.6-5 12.2-7.2 21.6 3.9 31 3 2.6 10.3 6.4 10.3 11 0 6-11.7-1.3-13-2.3 1.5 2.6 16.2 18 6.8 19.1-8.6 1-16.3-11.1-21.5-16.2-8.7-8.7-7.2 10.6-7.2 14.5-.1 6.3-4.5 19-12.5 10.7-6.6-6.8-4.1-17.8-8.7-25.4-5-8.3-13.3 8.3-15.4 11.4-2.3 3.4-14 19.9-18.6 11.1-3.8-7.1 2.2-18.3 5.2-24.8-1.1 2.3-8.7 5.8-10.9 6.9-5 2.5-10.1 4.1-15.8 3.7-11.7-.8-2.7-7 2-10.2"
                        fill="currentcolor"
                    />
                </svg>
                <svg className={svgStyles.spinner} viewBox={`0 0 ${CIRCLE_CONTAINER_SIZE} ${CIRCLE_CONTAINER_SIZE}`}>
                    <defs>
                        <linearGradient id="circle-gradient" x1="0" y1="15" x2="0" y2="135" gradientUnits="userSpaceOnUse">
                            <stop offset="0%" stopColor={colorScales.blue[500]} />
                            <stop offset="100%" stopColor={colorScales.purple[500]} />
                        </linearGradient>
                    </defs>
                    <circle
                        className={svgStyles.spinnerCircle}
                        style={{ animationDelay: `${spinnerInitialDelayMs}ms` }}
                        cx={CIRCLE_CONTAINER_SIZE / 2}
                        cy={CIRCLE_CONTAINER_SIZE / 2}
                        r={CIRCLE_RADIUS}
                        stroke="url(#circle-gradient)"
                        onAnimationStart={handleSpinnerAnimationStart}
                    />
                    <circle
                        className={svgStyles.spinnerCircle}
                        style={{ animationDelay: `${spinnerInitialDelayMs + spinnerDurationMs}ms` }}
                        cx={CIRCLE_CONTAINER_SIZE / 2}
                        cy={CIRCLE_CONTAINER_SIZE / 2}
                        r={CIRCLE_RADIUS}
                        stroke="url(#circle-gradient)"
                    />
                </svg>
            </div>
            <div className={styles.messageContainer}>
                <LoadingMessages show={showLoadingMessages && !showError} messages={messages} />
                {showError && <ErrorContent error={error} />}
            </div>
        </div>
    );
}

function ErrorContent({ error }: { error: LoadingError }) {
    const title = getErrorTitle(error.type);
    const description = getErrorDescription(error);
    return (
        <div className={styles.errorContent}>
            <h1 className={styles.errorTitle}>{title}</h1>
            <p className={styles.errorDescription}>{description}</p>
            <p className={styles.errorDescription}>We’ll keep trying to connect in the background.</p>
        </div>
    );
}

function getErrorTitle(errorType: LoadingError["type"]): string {
    switch (errorType) {
        case "server-unavailable":
            return "Unable to connect to the Octopus Server";
        case "network-error":
            return "Unable to connect to the network";
        default:
            return exhaustiveCheck(errorType, `Unknown error type: ${errorType}`);
    }
}

function getErrorDescription(error: LoadingError): string {
    let description = "There was an error when communicating with the Octopus Deploy server.";
    if (error.httpStatusCode && error.httpStatusCode > 0) {
        description += ` (${error.httpStatusCode})`;
    }
    return description;
}

const CIRCLE_STROKE_WIDTH = 2;
const CIRCLE_RADIUS = 74;
const CIRCLE_CIRCUMFERENCE = 2 * Math.PI * CIRCLE_RADIUS;
const CIRCLE_CONTAINER_SIZE = CIRCLE_RADIUS * 2 + CIRCLE_STROKE_WIDTH;

const animations = {
    octopusIcon: {
        scaleAndFade: keyframes({
            "0%": { transform: "scale(1)", opacity: 0 },
            "60%": { transform: "scale(1.125)", opacity: 1 },
            "100%": { transform: "scale(1)", opacity: 1 },
        }),
        color: keyframes({
            "0%": { color: "#DEE1E6" },
            "100%": { color: "#0D80D8" },
        }),
        pulse: keyframes({
            "0%": { opacity: 1 },
            "50%": { opacity: 0.5 },
            "100%": { opacity: 1 },
        }),
    },
    circle: {
        stroke: keyframes({
            "0%": { strokeDashoffset: CIRCLE_CIRCUMFERENCE, animationTimingFunction: "cubic-bezier(0.333,0.333,0.667,0.667)" },
            "50%": { strokeDashoffset: 0, animationTimingFunction: "step-end" },
            "51.67%": { strokeDashoffset: CIRCLE_CIRCUMFERENCE },
            "100%": { strokeDashoffset: CIRCLE_CIRCUMFERENCE },
        }),
        rotate: keyframes({
            "0%": { transform: "rotate(-101deg)", animationTimingFunction: "cubic-bezier(0.645,0.045,0.355,1)" },
            "50%": { transform: "rotate(619deg)", animationTimingFunction: "step-end" },
            "51.67%": { transform: "rotate(-101deg)" },
            "100%": { transform: "rotate(-101deg)" },
        }),
        fade: keyframes({
            "0%": { opacity: 1 },
            "50%": { opacity: 1 },
            "51.67%": { opacity: 0 },
            "100%": { opacity: 0 },
        }),
    },
    error: {
        fadeIn: keyframes({
            "0%": { opacity: 0 },
            "100%": { opacity: 1 },
        }),
    },
};

const styles = {
    container: css({
        position: "fixed",
        inset: 0,
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: space[32],
        height: "100vh",
        padding: space[40],
        backgroundColor: themeTokens.color.background.primary.default,
    }),
    spinnerContainer: css({
        position: "relative",
        display: "flex",
    }),
    messageContainer: css({
        position: "relative",
        width: "20rem",
    }),
    errorContent: css({
        position: "absolute",
        top: 0,
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        maxWidth: "20rem",
        gap: space[8],
        opacity: 0,
        animation: `200ms linear forwards ${animations.error.fadeIn}`,
    }),
    errorTitle: css({
        all: "unset",
        font: text.heading.small,
        color: themeTokens.color.text.primary,
    }),
    errorDescription: css({
        all: "unset",
        font: text.body.regular.medium,
        color: themeTokens.color.text.primary,
    }),
};

const spinnerInitialDelayMs = 900; // Starts when the icon reaches max size i.e. 60% * 1500ms = 900ms
const spinnerDurationMs = 3000;
const svgStyles = {
    spinner: css({
        width: 150,
        height: 150,
    }),
    octopusIcon: css({
        position: "absolute",
        inset: 0,
        margin: "auto",
        width: 58,
        height: 58,
        color: colorScales.slate[200],
        animation: [
            `${animations.octopusIcon.scaleAndFade} 1.5s cubic-bezier(0.215, 0.61, 0.355, 1) forwards`,
            `${animations.octopusIcon.color} 0.5s 1s cubic-bezier(0.645, 0.045, 0.355, 1) forwards`,
            `${animations.octopusIcon.pulse} 3s 1.5s ease-in-out infinite`,
        ].join(", "),
    }),
    spinnerCircle: css({
        fill: "none",
        strokeWidth: CIRCLE_STROKE_WIDTH,
        strokeLinecap: "round",
        strokeOpacity: 0.6,
        strokeDasharray: CIRCLE_CIRCUMFERENCE,
        strokeDashoffset: CIRCLE_CIRCUMFERENCE,
        transformOrigin: "50% 50%",
        animationName: `${animations.circle.stroke}, ${animations.circle.rotate}, ${animations.circle.fade}`,
        animationDuration: `${spinnerDurationMs * 2}ms`, // We have two overlapping spinner animations, one plays while the other fades and is idle so the duration is doubled
        animationTimingFunction: "linear",
        animationIterationCount: "infinite",
    }),
};

// Duplicated here to avoid additional dependencies in initial chunk
function exhaustiveCheck(param: never, message: string): never {
    throw new Error(message);
}
